"use client";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import {
  Bar,
  ComposedChart,
  CartesianGrid,
  Line,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { formatGranularDateTick } from "./metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";

const chartConfig: ChartConfig = {
  costDollars: {
    label: "Cost ($)",
    color: "#006e96",
  },
  costCumulativeDollars: {
    label: "Cumulative Cost ($)",
    color: "#eaaa00",
  },
};

type ResearchCostProps = {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
};

export const ResearchCost = (props: ResearchCostProps) => {
  const { parentDepartmentId } = useDepartmentStackContext();

  // Query
  const researchCostQuery = api.coreMetrics.researchCostPerDay.useQuery({
    startDate: props.startDate,
    endDate: props.endDate,
    granularity: props.granularity,
    parentDepartmentId: parentDepartmentId,
  });

  // Chart data
  const chartData = researchCostQuery.data?.chartData ?? [];
  const totalCostDollars = researchCostQuery.data?.totalCostDollars ?? 0;

  return (
    <div>
      <h2 className="mb-4">{"Research Cost"}</h2>
      {chartData.length === 0 && (
        <div className="text-gray-500">
          {"No data available for selected range."}
        </div>
      )}
      {chartData.length !== 0 && (
        <>
          <p className="mb-4 text-sm text-muted-foreground">
            {"Total Cost: $"}
            {totalCostDollars.toFixed(2)}
          </p>
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[200px] w-full"
          >
            <ComposedChart accessibilityLayer data={chartData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value: string) =>
                  formatGranularDateTick(value, props.granularity)
                }
              />
              <YAxis
                yAxisId="costDollars"
                dataKey={"costDollars"}
                orientation="left"
                tickFormatter={(value: number) => `$${value.toFixed(2)}`}
              />
              <YAxis
                yAxisId="costCumulativeDollars"
                dataKey={"costCumulativeDollars"}
                orientation="right"
                tickFormatter={(value: number) => `$${value.toFixed(2)}`}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    nameKey="date"
                    labelFormatter={(value: string) => value}
                    formatter={(value, name) => {
                      if (typeof value === "number") {
                        return [`$${value.toFixed(2)}`, name];
                      }
                      return [value, name];
                    }}
                  />
                }
              />
              <Legend />
              <Bar
                yAxisId="costDollars"
                dataKey={"costDollars"}
                name="Daily Cost ($)"
                fill="#006e96"
              />
              <Line
                yAxisId="costCumulativeDollars"
                dataKey={"costCumulativeDollars"}
                name="Cumulative Cost ($)"
                dot={false}
                stroke="#eaaa00"
              />
            </ComposedChart>
          </ChartContainer>
        </>
      )}
    </div>
  );
};
